import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import DSAView2D from "../components/dsa/scene2d/DSAView2D";
import { useDSAStore } from "../components/dsa/store/useDSAStore";
import { ALGORITHM_REGISTRY } from "../components/dsa/engine/AlgorithmRegistry";
import {
  grind75ByDifficulty,
  grind75Counts,
  leetcodeUrl,
} from "../components/dsa/engine/grind75";
import { THEME } from "../components/dsa/shared/colors";
import useWindowDimensions from "../hooks/useWindowDimension";

// Fundamentals — building-block visualizers (not Grind 75 problems).
const BASICS = [
  "array-traversal",
  "two-sum",
  "binary-search",
  "stack",
  "queue",
  "linked-list",
  "sliding-window",
  "bubble-sort",
  "selection-sort",
  "merge-sort",
  "quick-sort",
];

// Menu accent colors from the homepage palette: gold / mint / cream / coral
const MENU_COLORS = {
  Basics: { fg: "#F0C98A", bg: "rgba(251,219,147,0.10)", border: "rgba(251,219,147,0.28)" },
  Easy:   { fg: "#A8E6A0", bg: "rgba(219,255,203,0.12)", border: "rgba(219,255,203,0.32)" },
  Medium: { fg: THEME.cream, bg: "rgba(251,219,147,0.12)", border: "rgba(251,219,147,0.32)" },
  Hard:   { fg: "#FF8282", bg: "rgba(255,130,130,0.12)", border: "rgba(255,130,130,0.32)" },
  Trees:  { fg: "#80C8FF", bg: "rgba(16,96,160,0.12)",   border: "rgba(16,96,160,0.35)" },
  Graphs: { fg: "#D0A0FF", bg: "rgba(90,40,160,0.12)",   border: "rgba(90,40,160,0.35)" },
  Grid:   { fg: "#80FFCC", bg: "rgba(0,160,100,0.10)",   border: "rgba(0,200,120,0.30)" },
  DP:     { fg: "#FFB347", bg: "rgba(200,100,0,0.10)",   border: "rgba(255,140,40,0.30)" },
  Heap:   { fg: "#FF82CC", bg: "rgba(180,0,100,0.10)",   border: "rgba(255,80,160,0.30)" },
};

const LEGEND = [
  { state: "inactive", color: "#9A5560", label: "Inactive" },
  { state: "active", color: "#FBDB93", label: "Active" },
  { state: "compared", color: "#E8A020", label: "Compared" },
  { state: "visited", color: "#5C6BC0", label: "Visited" },
  { state: "answer", color: "#00EE77", label: "Answer" },
  { state: "rejected", color: "#DD2222", label: "Rejected" },
  { state: "pivot", color: "#DD7700", label: "Pivot" },
];

// ─── Popup shell ───────────────────────────────────────────────────────────
function Popup({ col, title, children }) {
  return (
    <div
      style={{
        position: "absolute",
        top: "calc(100% + 10px)",
        right: 0,
        background: "rgba(10,10,10,0.97)",
        border: `1px solid ${col.border}`,
        borderRadius: 14,
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 5,
        zIndex: 200,
        width: "min(330px, calc(100vw - 24px))",
        maxHeight: "74vh",
        overflowY: "auto",
        backdropFilter: "blur(16px)",
        boxShadow: `0 20px 60px rgba(0,0,0,0.7), 0 0 30px ${col.fg}18`,
      }}
    >
      <div
        style={{
          fontSize: 9,
          fontFamily: "'Orbitron', monospace",
          fontWeight: 700,
          letterSpacing: "0.12em",
          color: col.fg,
          textTransform: "uppercase",
          marginBottom: 4,
          paddingBottom: 8,
          borderBottom: `1px solid rgba(255,255,255,0.08)`,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

// A clickable row — a built visualizer
function BuiltRow({ label, meta, col, selected, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "8px 12px",
        borderRadius: 9,
        border: `1px solid ${selected ? col.border : "rgba(255,255,255,0.08)"}`,
        background: selected ? col.bg : "rgba(15,15,15,0.6)",
        color: selected ? col.fg : THEME.text,
        fontFamily: "'Raleway', system-ui, sans-serif",
        fontSize: 12.5,
        fontWeight: selected ? 600 : 400,
        cursor: "pointer",
        textAlign: "left",
        transition: "all 0.12s",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 10,
      }}
      onMouseEnter={(e) => {
        if (!selected) {
          e.currentTarget.style.background = "rgba(255,255,255,0.08)";
          e.currentTarget.style.color = THEME.cream;
        }
      }}
      onMouseLeave={(e) => {
        if (!selected) {
          e.currentTarget.style.background = "rgba(15,15,15,0.6)";
          e.currentTarget.style.color = THEME.text;
        }
      }}
    >
      <span style={{ lineHeight: 1.25 }}>{label}</span>
      {meta && (
        <span
          style={{
            fontSize: 10,
            color: THEME.dim,
            fontFamily: "monospace",
            flexShrink: 0,
          }}
        >
          {meta}
        </span>
      )}
    </button>
  );
}

// A not-yet-built Grind 75 row — links out to LeetCode, marked "soon"
function SoonRow({ problem }) {
  return (
    <a
      href={leetcodeUrl(problem.slug)}
      target="_blank"
      rel="noreferrer"
      style={{
        padding: "8px 12px",
        borderRadius: 9,
        border: "1px solid rgba(190,91,80,0.1)",
        background: "transparent",
        color: THEME.dim,
        fontFamily: "'Raleway', system-ui, sans-serif",
        fontSize: 12.5,
        textDecoration: "none",
        cursor: "pointer",
        textAlign: "left",
        transition: "all 0.12s",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 10,
        opacity: 0.72,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "rgba(15,15,15,0.6)";
        e.currentTarget.style.opacity = 1;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.opacity = 0.72;
      }}
    >
      <span style={{ lineHeight: 1.25 }}>
        <span
          style={{
            color: "rgba(190,91,80,0.75)",
            fontFamily: "monospace",
            fontSize: 10,
            marginRight: 6,
          }}
        >
          #{problem.num}
        </span>
        {problem.title}
      </span>
      <span
        style={{
          fontSize: 8,
          color: "rgba(190,91,80,0.6)",
          flexShrink: 0,
          fontFamily: "'Orbitron', monospace",
          letterSpacing: "0.06em",
        }}
      >
        SOON ↗
      </span>
    </a>
  );
}

// Rows for a given category tab — shared between the desktop popup and the
// mobile drawer so the two surfaces never drift apart.
function categoryRows(tab, col, currentId, onPick) {
  if (tab === "Basics") {
    return BASICS.map((id) => {
      const a = ALGORITHM_REGISTRY[id];
      if (!a) return null;
      return (
        <BuiltRow
          key={id}
          label={a.label}
          meta={a.complexity.time}
          col={col}
          selected={currentId === id}
          onClick={() => onPick(a)}
        />
      );
    });
  }

  if (tab === "Trees" || tab === "Graphs" || tab === "Grid" || tab === "DP" || tab === "Heap") {
    return Object.values(ALGORITHM_REGISTRY)
      .filter((a) => a.category === tab)
      .map((a) => (
        <BuiltRow
          key={a.id}
          label={a.label}
          meta={a.complexity.time}
          col={col}
          selected={currentId === a.id}
          onClick={() => onPick(a)}
        />
      ));
  }

  // Grind 75 difficulty tabs
  return grind75ByDifficulty(tab).map((p) => {
    const a = p.builtId ? ALGORITHM_REGISTRY[p.builtId] : null;
    if (a) {
      return (
        <BuiltRow
          key={p.num}
          label={
            <>
              <span style={{ color: col.fg, fontFamily: "monospace", fontSize: 10, marginRight: 6 }}>
                #{p.num}
              </span>
              {p.title}
            </>
          }
          meta={a.complexity.time}
          col={col}
          selected={currentId === p.builtId}
          onClick={() => onPick(a)}
        />
      );
    }
    return <SoonRow key={p.num} problem={p} />;
  });
}

function categoryTitle(tab) {
  if (tab === "Basics") return "Fundamentals";
  if (tab === "Easy" || tab === "Medium" || tab === "Hard") {
    const counts = grind75Counts(tab);
    return `Grind 75 · ${tab} · ${counts.built}/${counts.total} built`;
  }
  return tab;
}

const MENU_TABS = ["Basics", "Trees", "Graphs", "Grid", "DP", "Heap", "Easy", "Medium", "Hard"];

// ─── Mobile drawer — menu icon → category list → problem list ──────────────
function MobileProblemMenu({ onSelect, currentId }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState(null);

  const close = () => {
    setOpen(false);
    setCategory(null);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Browse problems"
        style={{
          width: 38,
          height: 38,
          borderRadius: 10,
          border: "1px solid rgba(255,255,255,0.12)",
          background: "rgba(15,15,15,0.85)",
          color: THEME.cream,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>

      {open && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 300,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "flex-end",
          }}
          onClick={close}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxHeight: "78vh",
              overflowY: "auto",
              background: "rgba(10,10,10,0.98)",
              borderTop: "1px solid rgba(255,255,255,0.12)",
              borderRadius: "16px 16px 0 0",
              padding: "14px 14px 24px",
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            {category === null ? (
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontFamily: "'Orbitron', monospace", fontSize: 10, letterSpacing: "0.12em", color: THEME.dim, textTransform: "uppercase" }}>
                    Browse problems
                  </span>
                  <button onClick={close} style={{ background: "none", border: "none", color: THEME.dim, fontSize: 18, cursor: "pointer", lineHeight: 1 }}>×</button>
                </div>
                {MENU_TABS.map((tab) => {
                  const col = MENU_COLORS[tab];
                  const isGrind = tab === "Easy" || tab === "Medium" || tab === "Hard";
                  const suffix = isGrind ? (() => { const c = grind75Counts(tab); return `${c.built}/${c.total}`; })() : "";
                  return (
                    <button
                      key={tab}
                      onClick={() => setCategory(tab)}
                      style={{
                        padding: "12px 14px",
                        borderRadius: 10,
                        border: `1px solid ${col.border}`,
                        background: col.bg,
                        color: col.fg,
                        fontFamily: "'Orbitron', monospace",
                        fontSize: 12,
                        fontWeight: 600,
                        letterSpacing: "0.06em",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      {tab}
                      {suffix && <span style={{ fontSize: 10, opacity: 0.85 }}>{suffix}</span>}
                    </button>
                  );
                })}
              </>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                  <button
                    onClick={() => setCategory(null)}
                    style={{
                      background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                      color: THEME.dim, fontFamily: "'Orbitron', monospace", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase",
                    }}
                  >
                    ← Back
                  </button>
                  <button onClick={close} style={{ background: "none", border: "none", color: THEME.dim, fontSize: 18, cursor: "pointer", lineHeight: 1 }}>×</button>
                </div>
                <div style={{ fontSize: 9, fontFamily: "'Orbitron', monospace", fontWeight: 700, letterSpacing: "0.12em", color: MENU_COLORS[category].fg, textTransform: "uppercase", marginBottom: 4, paddingBottom: 8, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                  {categoryTitle(category)}
                </div>
                {categoryRows(category, MENU_COLORS[category], currentId, (a) => {
                  onSelect(a);
                  close();
                })}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─── Problem menu — Basics + Grind 75 (Easy / Medium / Hard) ────────────────
function ProblemMenu({ onSelect, currentId }) {
  const [open, setOpen] = useState(null);
  const menuRef = useRef();
  const { width } = useWindowDimensions();
  const isMobile = width < 640;

  useEffect(() => {
    const handler = (e) => {
      if (!menuRef.current?.contains(e.target)) setOpen(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (isMobile) {
    return <MobileProblemMenu onSelect={onSelect} currentId={currentId} />;
  }

  const tabs = MENU_TABS;

  return (
    <div
      ref={menuRef}
      className="no-scrollbar"
      style={{
        display: "flex",
        gap: 8,
        position: "relative",
        overflowX: "auto",
        maxWidth: "100%",
        WebkitOverflowScrolling: "touch",
      }}
    >
      {tabs.map((tab) => {
        const col = MENU_COLORS[tab];
        const isOpen = open === tab;
        const isGrind = tab === "Easy" || tab === "Medium" || tab === "Hard";
        const counts = isGrind ? grind75Counts(tab) : null;
        const suffix = counts ? `${counts.built}/${counts.total}` : "";

        return (
          <div key={tab} style={{ position: "relative", flexShrink: 0 }}>
            <button
              onClick={() => setOpen(isOpen ? null : tab)}
              style={{
                padding: "6px 14px",
                borderRadius: 999,
                border: `1px solid ${isOpen ? col.border : "rgba(255,255,255,0.12)"}`,
                background: isOpen ? col.bg : "rgba(15,15,15,0.85)",
                color: isOpen ? col.fg : THEME.dim,
                fontFamily: "'Orbitron', monospace",
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.08em",
                cursor: "pointer",
                transition: "all 0.15s",
                display: "flex",
                alignItems: "center",
                gap: 6,
                whiteSpace: "nowrap",
              }}
            >
              {tab}
              {suffix && (
                <span
                  style={{
                    fontSize: 8,
                    color: isOpen ? col.fg : "rgba(190,91,80,0.75)",
                    opacity: 0.85,
                  }}
                >
                  {suffix}
                </span>
              )}
            </button>

            {isOpen && tab === "Basics" && (
              <Popup col={col} title="Fundamentals">
                {BASICS.map((id) => {
                  const a = ALGORITHM_REGISTRY[id];
                  if (!a) return null;
                  return (
                    <BuiltRow
                      key={id}
                      label={a.label}
                      meta={a.complexity.time}
                      col={col}
                      selected={currentId === id}
                      onClick={() => {
                        onSelect(a);
                        setOpen(null);
                      }}
                    />
                  );
                })}
              </Popup>
            )}

            {isOpen && (tab === "Trees" || tab === "Graphs" || tab === "Grid" || tab === "DP" || tab === "Heap") && (
              <Popup col={col} title={tab}>
                {Object.values(ALGORITHM_REGISTRY)
                  .filter((a) => a.category === tab)
                  .map((a) => (
                    <BuiltRow
                      key={a.id}
                      label={a.label}
                      meta={a.complexity.time}
                      col={col}
                      selected={currentId === a.id}
                      onClick={() => {
                        onSelect(a);
                        setOpen(null);
                      }}
                    />
                  ))}
              </Popup>
            )}

            {isOpen && isGrind && (
              <Popup col={col} title={`Grind 75 · ${tab} · ${suffix} built`}>
                {grind75ByDifficulty(tab).map((p) => {
                  const a = p.builtId ? ALGORITHM_REGISTRY[p.builtId] : null;
                  if (a) {
                    return (
                      <BuiltRow
                        key={p.num}
                        label={
                          <>
                            <span
                              style={{
                                color: col.fg,
                                fontFamily: "monospace",
                                fontSize: 10,
                                marginRight: 6,
                              }}
                            >
                              #{p.num}
                            </span>
                            {p.title}
                          </>
                        }
                        meta={a.complexity.time}
                        col={col}
                        selected={currentId === p.builtId}
                        onClick={() => {
                          onSelect(a);
                          setOpen(null);
                        }}
                      />
                    );
                  }
                  return <SoonRow key={p.num} problem={p} />;
                })}
              </Popup>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Legend row ────────────────────────────────────────────────────────────
function LegendRow() {
  return (
    <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
      {LEGEND.map(({ color, label }) => (
        <div
          key={label}
          style={{ display: "flex", alignItems: "center", gap: 5 }}
        >
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: 2,
              background: color,
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: 10,
              color: THEME.dim,
              fontFamily: "'Raleway', system-ui, sans-serif",
            }}
          >
            {label}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Bottom HUD ────────────────────────────────────────────────────────────
function BottomHUD({ algo }) {
  const isPlaying = useDSAStore((s) => s.isPlaying);
  const speed = useDSAStore((s) => s.speed);
  const currentStep = useDSAStore((s) => s.currentStep);
  const steps = useDSAStore((s) => s.steps);
  const setPlaying = useDSAStore((s) => s.setPlaying);
  const setSpeed = useDSAStore((s) => s.setSpeed);
  const stepForward = useDSAStore((s) => s.stepForward);
  const reset = useDSAStore((s) => s.reset);
  const { width } = useWindowDimensions();
  const isMobile = width < 640;

  const isDone = currentStep >= steps.length && steps.length > 0;
  const progress = steps.length > 0 ? currentStep / steps.length : 0;
  const SPEEDS = [0.5, 1, 1.5, 2, 3];

  const iconStyle = {
    width: 28,
    height: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.1)",
    background: "rgba(15,15,15,0.85)",
    color: THEME.dim,
    cursor: "pointer",
    transition: "all 0.15s",
    fontFamily: "monospace",
    fontSize: 11,
  };

  const primaryBtn = {
    ...iconStyle,
    padding: "0 18px",
    width: "auto",
    gap: 7,
    border: `1px solid rgba(251,219,147,0.4)`,
    background: "rgba(251,219,147,0.1)",
    color: isDone ? THEME.dim : THEME.amber,
    fontFamily: "'Orbitron', monospace",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.08em",
    opacity: isDone ? 0.5 : 1,
  };

  return (
    <div
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        padding: isMobile ? "0 14px 14px" : "0 28px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        pointerEvents: "all",
      }}
    >
      {/* Progress bar */}
      <div
        style={{
          height: 1.5,
          background: "rgba(255,255,255,0.08)",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${progress * 100}%`,
            background: `linear-gradient(90deg, ${THEME.amber}, #A8E6A0)`,
            transition: "width 0.2s",
          }}
        />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: isMobile ? "wrap" : "nowrap" }}>
        {/* Step counter */}
        <span
          style={{
            fontFamily: "'Orbitron', monospace",
            fontSize: 9,
            color: THEME.dim,
            letterSpacing: "0.1em",
            marginRight: 4,
          }}
        >
          {currentStep} / {steps.length}
        </span>
        {isDone && (
          <span
            style={{
              fontFamily: "'Raleway', sans-serif",
              fontSize: 11,
              color: "#A8E6A0",
              fontWeight: 700,
            }}
          >
            ✓ Complete
          </span>
        )}

        {/* Controls */}
        <div style={{ display: "flex", gap: 6, marginLeft: 4 }}>
          {/* Reset */}
          <button
            style={iconStyle}
            onClick={reset}
            title="Reset"
            onMouseEnter={(e) => (e.currentTarget.style.color = THEME.cream)}
            onMouseLeave={(e) => (e.currentTarget.style.color = THEME.dim)}
          >
            ↺
          </button>

          {/* Play/Pause */}
          <button
            style={primaryBtn}
            onClick={() => !isDone && setPlaying(!isPlaying)}
            disabled={isDone}
          >
            {isPlaying ? (
              <>
                <span style={{ fontSize: 10 }}>⏸</span> Pause
              </>
            ) : (
              <>
                <span style={{ fontSize: 10 }}>▶</span> Play
              </>
            )}
          </button>

          {/* Step */}
          <button
            style={iconStyle}
            onClick={stepForward}
            disabled={isDone}
            title="Step"
            onMouseEnter={(e) => (e.currentTarget.style.color = THEME.cream)}
            onMouseLeave={(e) => (e.currentTarget.style.color = THEME.dim)}
          >
            ⏭
          </button>
        </div>

        {/* Spacer */}
        {!isMobile && <div style={{ flex: 1 }} />}

        {/* Legend — desktop only; on mobile it's the first thing to go to
            make room for the controls everyone actually needs. */}
        {!isMobile && <LegendRow />}

        {/* Spacer */}
        {!isMobile && <div style={{ flex: 1 }} />}

        {/* Speed */}
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span
            style={{
              fontFamily: "'Orbitron', monospace",
              fontSize: 9,
              color: THEME.dim,
              letterSpacing: "0.1em",
            }}
          >
            SPEED
          </span>
          {SPEEDS.map((s) => (
            <button
              key={s}
              onClick={() => setSpeed(s)}
              style={{
                padding: "4px 8px",
                borderRadius: 6,
                border: `1px solid ${speed === s ? "rgba(251,219,147,0.5)" : "rgba(255,255,255,0.10)"}`,
                background:
                  speed === s ? "rgba(251,219,147,0.12)" : "transparent",
                color: speed === s ? THEME.amber : THEME.dim,
                fontFamily: "'Orbitron', monospace",
                fontSize: 9,
                cursor: "pointer",
                transition: "all 0.12s",
              }}
            >
              {s}×
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Scene info overlay (operation / explanation / vars / call stack) ──────
function SceneInfoOverlay() {
  const operation  = useDSAStore((s) => s.operation);
  const explanation = useDSAStore((s) => s.explanation);
  const variables  = useDSAStore((s) => s.variables);
  const callStack  = useDSAStore((s) => s.callStack);
  const varEntries = Object.entries(variables);
  const { width } = useWindowDimensions();
  const isMobile = width < 640;

  return (
    <>
      {/* Centered text — below the top bar */}
      <div
        style={{
          position: "absolute",
          top: isMobile ? 54 : 62,
          left: 0,
          right: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
          pointerEvents: "none",
          zIndex: 50,
          padding: "0 16px",
        }}
      >
        {operation && (
          <div style={{
            padding: isMobile ? "7px 16px" : "9px 26px",
            borderRadius: 999,
            background: "rgba(251,219,147,0.14)",
            border: "1px solid rgba(251,219,147,0.42)",
            color: THEME.cream,
            fontFamily: "'Orbitron', 'Audiowide', monospace",
            fontSize: isMobile ? 11 : 13,
            fontWeight: 700,
            letterSpacing: "0.06em",
            backdropFilter: "blur(10px)",
            whiteSpace: isMobile ? "normal" : "nowrap",
            textAlign: "center",
            boxShadow: "0 4px 24px rgba(251,219,147,0.12)",
          }}>
            {operation}
          </div>
        )}

        {explanation && (
          <div style={{
            padding: isMobile ? "8px 16px" : "10px 22px",
            borderRadius: 12,
            background: "rgba(0,0,0,0.82)",
            border: "1px solid rgba(255,255,255,0.12)",
            color: THEME.text,
            fontFamily: "'Raleway', system-ui, sans-serif",
            fontSize: isMobile ? 12 : 13,
            fontWeight: 500,
            lineHeight: 1.5,
            textAlign: "center",
            backdropFilter: "blur(14px)",
            maxWidth: isMobile ? "100%" : 620,
          }}>
            {explanation}
          </div>
        )}

        {varEntries.length > 0 && (
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap", justifyContent: "center" }}>
            {varEntries.map(([k, v]) => (
              <div key={k} style={{
                padding: "4px 12px",
                borderRadius: 7,
                background: "rgba(15,15,15,0.85)",
                border: "1px solid rgba(190,91,80,0.28)",
                fontFamily: "monospace",
                fontSize: 12,
                color: THEME.creamSoft,
                backdropFilter: "blur(6px)",
              }}>
                <span style={{ color: THEME.dim }}>{k}</span>
                <span style={{ color: "rgba(246,230,208,0.4)", margin: "0 5px" }}>=</span>
                <span style={{ color: THEME.cream, fontWeight: 700 }}>{String(v)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Call stack — left side panel. Hidden on mobile: it would sit
          directly under the centered operation/explanation text, which
          takes the full width on a narrow screen. */}
      {callStack.length > 0 && !isMobile && (
        <div style={{
          position: "absolute",
          top: 70,
          left: 20,
          display: "flex",
          flexDirection: "column",
          gap: 5,
          pointerEvents: "none",
          zIndex: 50,
          width: 180,
        }}>
          <div style={{
            fontSize: 9,
            color: THEME.dim,
            fontFamily: "'Orbitron', monospace",
            letterSpacing: "0.12em",
            marginBottom: 2,
            textTransform: "uppercase",
          }}>
            Call Stack
          </div>
          {callStack.slice(0, 10).map((frame, i) => (
            <div key={i} style={{
              padding: "5px 11px",
              borderRadius: 7,
              background: i === 0 ? "rgba(251,219,147,0.16)" : "rgba(15,15,15,0.78)",
              border: `1px solid ${i === 0 ? "rgba(251,219,147,0.45)" : "rgba(255,255,255,0.08)"}`,
              color: i === 0 ? THEME.cream : THEME.dim,
              fontFamily: "monospace",
              fontSize: 12,
              opacity: Math.max(0.28, 1 - i * 0.12),
              backdropFilter: "blur(8px)",
            }}>
              {frame}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ─── Main DSA Page ─────────────────────────────────────────────────────────
export default function DSA() {
  const selectedAlgorithm = useDSAStore((s) => s.selectedAlgorithm);
  const setAlgorithm = useDSAStore((s) => s.setAlgorithm);
  const loadSteps = useDSAStore((s) => s.loadSteps);
  const isPlaying = useDSAStore((s) => s.isPlaying);
  const speed = useDSAStore((s) => s.speed);
  const stepForward = useDSAStore((s) => s.stepForward);
  const setPlaying = useDSAStore((s) => s.setPlaying);
  const intervalRef = useRef(null);
  const BASE_DELAY_MS = 900;
  const { width } = useWindowDimensions();
  const isMobile = width < 640;

  const algo = ALGORITHM_REGISTRY[selectedAlgorithm];

  // Load steps when algorithm changes
  useEffect(() => {
    if (!algo) return;
    const newSteps = algo.getSteps();
    loadSteps(newSteps, {}, algo.complexity);
    // Land on the post-init frame (currentStep = 1), matching the reset button.
    // Deferred so the lazy-loaded 3D scene has mounted before state lands (else the
    // first frame paints empty). reset() recomputes from step 0, so it's idempotent
    // under StrictMode's double-invoke.
    const t = setTimeout(() => useDSAStore.getState().reset(), 60);
    return () => clearTimeout(t);
  }, [selectedAlgorithm]);

  // Playback loop
  useEffect(() => {
    if (isPlaying) {
      const delay = BASE_DELAY_MS / speed;
      intervalRef.current = setInterval(() => {
        const ok = useDSAStore.getState().stepForward();
        if (!ok) {
          setPlaying(false);
          clearInterval(intervalRef.current);
        }
      }, delay);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPlaying, speed]);

  const handleSelect = (a) => {
    setAlgorithm(a.category, a.id);
  };

  return (
    <>
    <Helmet>
      <title>DSA Visualiser — Shreeram Ganeshan</title>
      <meta name="description" content="Interactive data structures and algorithms visualiser — step through sorting, searching, linked lists, and more." />
      <meta property="og:title" content="DSA Visualiser — Shreeram Ganeshan" />
      <meta property="og:description" content="Step-by-step visualiser for arrays, binary search, linked lists, stacks, queues, and sliding window algorithms." />
      <meta property="og:url" content="https://shreeram.dev/dsa" />
    </Helmet>
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "#0a0a0a",
        fontFamily: "'Raleway', system-ui, sans-serif",
        overflow: "hidden",
      }}
    >
      {/* ── 2D Scene — full screen ── */}
      <div style={{ position: "absolute", inset: 0 }}>
        <DSAView2D />
      </div>

      {/* ── Top bar overlay ── */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: isMobile ? 46 : 54,
          display: "flex",
          alignItems: "center",
          padding: isMobile ? "0 12px" : "0 24px",
          gap: isMobile ? 10 : 20,
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.94) 0%, rgba(22,10,14,0) 100%)",
          pointerEvents: "all",
          zIndex: 100,
        }}
      >
        {/* Logo + "Algorithm Visualizer" — desktop only, the top bar is too
            tight on mobile to spare the space for branding. */}
        {!isMobile && (
          <>
            <span
              style={{
                fontFamily: "'Orbitron', 'Audiowide', monospace",
                fontSize: 13,
                fontWeight: 700,
                color: THEME.amber,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
              }}
            >
              DSA
            </span>
            <span
              style={{ width: 1, height: 16, background: "rgba(255,255,255,0.12)" }}
            />
            <span
              style={{
                fontFamily: "'Raleway', system-ui, sans-serif",
                fontSize: 11,
                color: THEME.dim,
                letterSpacing: "0.04em",
              }}
            >
              Algorithm Visualizer
            </span>
          </>
        )}

        {/* Active algo name */}
        {algo && (
          <>
            {!isMobile && (
              <span
                style={{
                  width: 1,
                  height: 16,
                  background: "rgba(255,255,255,0.10)",
                }}
              />
            )}
            <span
              style={{
                fontFamily: "'Raleway', system-ui, sans-serif",
                fontSize: isMobile ? 11 : 12,
                color: THEME.text,
                fontWeight: 500,
                whiteSpace: "nowrap",
              }}
            >
              {algo.label}
            </span>
            {!isMobile && (
              <span
                style={{
                  fontFamily: "monospace",
                  fontSize: 10,
                  color: THEME.dim,
                  background: "rgba(15,15,15,0.85)",
                  padding: "2px 8px",
                  borderRadius: 4,
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                {algo.complexity.time} · {algo.complexity.space}
              </span>
            )}
          </>
        )}

        <div style={{ flex: 1 }} />

        {/* Problem menu — Basics + Grind 75 */}
        <ProblemMenu onSelect={handleSelect} currentId={selectedAlgorithm} />
      </div>

      {/* ── Scene info overlay (operation / explanation / vars / call stack) ── */}
      <SceneInfoOverlay />

      {/* ── Bottom HUD ── */}
      <BottomHUD algo={algo} />
    </div>
    </>
  );
}
